<template>
  <v-card
    :id="`thermostat_${ThermostatInfo.controlId}`"
    class="thermostat-tile rounded-2 pa-3"
    :class="{ 'thermostat-tile--attention': isOutOfRange }"
    elevation="2"
    data-cy="thermostat-tile"
    @click="showConfigDialog = true"
  >
    <!--Header: status + name + set by-->
    <div class="d-flex align-center justify-space-between">
      <v-tooltip location="top">
        <template v-slot:activator="{ props }">
          <span
            class="text-primary text-subtitle-2 font-weight-bold text-truncate tile-name"
            v-bind="props"
          >
            <span class="status-dot mr-1" :class="statusDotClass" />
            {{ ThermostatInfo.controlInfo.friendlyName }}
          </span>
        </template>
        <span>{{ ThermostatInfo.controlInfo.friendlyName }}</span>
      </v-tooltip>
      <span
        v-if="ThermostatInfo.setBy"
        class="text-sonicSilver text-caption ml-2"
      >
        {{ ThermostatInfo.setBy }}
      </span>
    </div>
    <div class="text-sonicSilver text-caption">
      {{ siteName }}
    </div>
    <!--Temperature-->
    <div
      class="text-h4 font-weight-bold mt-1 tile-temp"
      :class="`text-${activityColor}`"
      data-cy="tile-temperature"
    >
      {{ ThermostatInfo.temperature ?? '—' }}&deg;
    </div>
    <!--Action line-->
    <div
      class="text-caption font-weight-bold tile-action"
      data-cy="tile-action"
    >
      <template v-if="activity === 'heating' || activity === 'cooling'">
        <span :class="`text-${activityColor}`">
          {{ activity === 'heating' ? 'Heating' : 'Cooling' }}
        </span>
        <span class="text-sonicSilver font-weight-regular">
          to {{ actionTarget }}&deg;
        </span>
      </template>
      <span v-else-if="activity === 'off'" class="text-sonicSilver">
        System off
      </span>
      <span v-else class="text-sonicSilver">Space satisfied</span>
    </div>
    <!--Mode / fan / keypad summary-->
    <div class="text-caption tile-summary" data-cy="tile-summary">
      <span class="font-weight-bold" :class="`text-${modeColor}`">
        {{ modeSummary }}
      </span>
      <span class="text-sonicSilver">
        · Fan {{ ThermostatInfo.fanStatus }} ·
        {{
          isKeypadUnlocked(ThermostatInfo.frontKeypad) ? 'unlocked' : 'locked'
        }}
      </span>
    </div>
    <!--Attention chip-->
    <v-chip
      v-if="isOutOfRange"
      size="small"
      color="warning"
      variant="tonal"
      class="mt-2 font-weight-bold"
      data-cy="tile-alert"
    >
      Settings out of range
    </v-chip>
    <!--Configure dialog-->
    <ThermostatConfigDialog
      v-if="showConfigDialog"
      :dialog="showConfigDialog"
      :thermostatInfo="ThermostatInfo"
      @update:dialog="(value) => (showConfigDialog = value)"
      @set-thermostat-config="$emit('set-thermostat-config')"
    />
  </v-card>
</template>

<script>
import {
  ThermostatDeviceSubType,
  MILESIGHT_TARGET_RANGE,
  isKeypadUnlocked,
  getThermostatActivity,
} from '@/Core.Service.Domain/Controls/Thermostats/types/Thermostat';

import ThermostatConfigDialog from '@/Core.Service.Domain/Controls/Thermostats/Components/ThermostatConfigDialog.vue';

import { displaySiteName } from '@/configuration/featureFlags';

export default {
  name: 'ThermostatCard',

  components: {
    ThermostatConfigDialog,
  },

  props: {
    ThermostatInfo: {
      type: Object,
      required: true,
    },
  },

  emits: ['set-thermostat-config'],

  data() {
    return {
      showConfigDialog: false,
    };
  },

  mounted() {
    this.$nextTick(() => {
      const controlAnchorPosition = localStorage.getItem(
        'controlAnchorPosition'
      );
      if (controlAnchorPosition) {
        window.scrollTo({
          top: document.querySelector(controlAnchorPosition)?.offsetTop - 150,
          behavior: 'smooth',
        });
      }
    });
  },

  computed: {
    // Redacted in the demo environment so recordings never show a real site.
    siteName() {
      return displaySiteName(this.ThermostatInfo.controlInfo.siteLocationName);
    },

    isMilesight() {
      return (
        this.ThermostatInfo.deviceSubTypeId ===
        ThermostatDeviceSubType.MilesightLoRa
      );
    },

    activity() {
      return getThermostatActivity(
        this.ThermostatInfo.runStatus,
        this.ThermostatInfo.mode
      );
    },

    activityColor() {
      if (this.activity === 'heating') return 'heatColor';
      if (this.activity === 'cooling') return 'intechBlue';
      return 'sonicSilver';
    },

    actionTarget() {
      if (this.isMilesight)
        return Math.round(this.ThermostatInfo.targetTemperature);
      return this.activity === 'heating'
        ? this.ThermostatInfo.heatSetting
        : this.ThermostatInfo.coolSetting;
    },

    modeColor() {
      if (this.ThermostatInfo.mode === 'Heat') return 'heatColor';
      if (this.ThermostatInfo.mode === 'EM Heat') return 'heatColor';
      if (this.ThermostatInfo.mode === 'Cool') return 'intechBlue';
      return 'primary';
    },

    modeSummary() {
      const { mode, heatSetting, coolSetting, targetTemperature } =
        this.ThermostatInfo;
      if (mode === 'Off') return 'Off';
      if (this.isMilesight)
        return `${mode} at ${Math.round(targetTemperature)}°`;
      if (mode === 'Heat') return `Heat at ${heatSetting}°`;
      if (mode === 'Cool') return `Cool at ${coolSetting}°`;
      return `Auto ${heatSetting}–${coolSetting}°`;
    },

    isOutOfRange() {
      const {
        coolSetting,
        heatSetting,
        targetTemperature,
        minHeatSetting,
        maxHeatSetting,
        minCoolSetting,
        maxCoolSetting,
      } = this.ThermostatInfo;

      if (this.isMilesight) {
        return (
          targetTemperature < MILESIGHT_TARGET_RANGE.min ||
          targetTemperature > MILESIGHT_TARGET_RANGE.max
        );
      }

      return (
        heatSetting < minHeatSetting ||
        heatSetting > maxHeatSetting ||
        coolSetting < minCoolSetting ||
        coolSetting > maxCoolSetting
      );
    },

    statusDotClass() {
      return this.isOutOfRange ? 'status-dot--warning' : 'status-dot--ok';
    },
  },

  methods: {
    isKeypadUnlocked,
  },
};
</script>

<style lang="scss" scoped>
.thermostat-tile {
  cursor: pointer;
  min-height: 128px;
  transition:
    box-shadow 0.12s,
    transform 0.12s;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 40, 85, 0.18) !important;
    transform: translateY(-1px);
  }
}

.thermostat-tile--attention {
  border-left: 6px solid rgb(var(--v-theme-warning));
}

.tile-name {
  max-width: 200px;
}

.tile-temp {
  line-height: 1.1;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  vertical-align: middle;
}

.status-dot--ok {
  background-color: rgb(var(--v-theme-greenGraph));
}

.status-dot--warning {
  background-color: rgb(var(--v-theme-warning));
}
</style>
